import _ from 'underscore';

import * as ArchiveActions from 'archive/ArchiveActions';
import {NOT_ARCHIVED_PURPOSE_STRING} from 'archive/ArchiveConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {BenchlingAction} from 'redux-stores/Action';
import {AssayRecordActionType} from 'results/assays/AssayRecordActions';
import {AssayRun} from 'results/assays/AssayRecordConstants';
import {Filter, FilterKey, keyToEmptyFilter, MultiValueFilter} from 'search/SearchConstants';

import {AvailabilityStatus, EntriesByRunId} from './RunsInboxModalConstants';
import {RunsInboxModalActionType} from './RunsInboxModalReduxActions';

export interface RunsInboxModalState {
  assayRunIds: Array<string>;
  entriesByRunId: EntriesByRunId;
  isLoading: boolean;
  isLoadingMore: boolean;
  offset: number;
  total: number;
  selectedRunId: string | null;
  filters: Array<Filter>;
  requestId: string | null;
}
function getDefaultFilters(): Array<Filter> {
  const filters: Array<Filter> = [
    {
      ...keyToEmptyFilter(FilterKey.TYPES),
      value: [ModelTypeConstants.ASSAY_RUN],
    },
    {
      ...keyToEmptyFilter(FilterKey.RUN_AVAILABILITY),
      value: AvailabilityStatus.AVAILABLE,
    },
    {
      ...keyToEmptyFilter(FilterKey.ARCHIVE_PURPOSES),
      value: [NOT_ARCHIVED_PURPOSE_STRING],
    },
  ];
  return filters;
}
const RUNS_INBOX_MODAL_INITIAL_STATE: RunsInboxModalState = {
  assayRunIds: [],
  entriesByRunId: {},
  isLoading: true,
  isLoadingMore: false,
  offset: 0,
  total: 0,
  filters: getDefaultFilters(),
  selectedRunId: null,
  requestId: null,
};

function reduceRunsInboxModal(
  state: RunsInboxModalState = RUNS_INBOX_MODAL_INITIAL_STATE,
  action: BenchlingAction
): RunsInboxModalState {
  // Using the non-Typescript AssayRecordAction actions with the Typescript RunsInboxModalActionType
  // actions in the same switch statement causes TS to error because it cannot resolve members of the unioned
  // BenchlingActions. Migrating AssayRecordAction is a lot of work so for now keep them separate.
  switch (action.type) {
    case AssayRecordActionType.ASSAY_RUN_SEARCH_STARTED:
      return {...state, assayRunIds: [], isLoading: true, requestId: action.requestId};
    case AssayRecordActionType.ASSAY_RUN_FETCH_MORE_RESULTS_STARTED:
      return {...state, isLoadingMore: true, requestId: action.requestId};
    case AssayRecordActionType.ASSAY_RUN_SEARCH_COMPLETED: {
      if (action.requestId !== state.requestId) {
        return state;
      }
      const {assayRuns, entriesByRunId, offset, total} = action;
      const assayRunIds = assayRuns.map((assayRun: AssayRun) => assayRun.id);
      const newAssayRunIds = state.isLoadingMore
        ? _.unique([...state.assayRunIds, ...assayRunIds])
        : assayRunIds;
      return {
        ...state,
        assayRunIds: newAssayRunIds,
        entriesByRunId: {...state.entriesByRunId, ...entriesByRunId},
        selectedRunId: newAssayRunIds.includes(state.selectedRunId!) ? state.selectedRunId : null,
        isLoading: false,
        isLoadingMore: false,
        offset,
        total,
      };
    }
    case ArchiveActions.ARCHIVE_RUNS_COMPLETED:
    case ArchiveActions.UNARCHIVE_RUNS_COMPLETED: {
      const {ids} = action;
      const changedRunIdsFromRunsInboxModal = ids.filter((id: string) => state.assayRunIds.includes(id));
      if (changedRunIdsFromRunsInboxModal.length) {
        const archived = action.type === ArchiveActions.ARCHIVE_RUNS_COMPLETED;
        const currentArchiveFilter = state.filters.find(
          (filter) => filter.key === FilterKey.ARCHIVE_PURPOSES
        ) as MultiValueFilter;
        const runStatusAndFilterMatch =
          currentArchiveFilter == null ||
          (archived && currentArchiveFilter.value.includes(action.purpose)) ||
          (!archived && currentArchiveFilter.value.includes(NOT_ARCHIVED_PURPOSE_STRING));
        return {
          ...state,
          selectedRunId: null,
          assayRunIds: runStatusAndFilterMatch
            ? [...state.assayRunIds]
            : state.assayRunIds.filter((id) => !changedRunIdsFromRunsInboxModal.includes(id)),
        };
      }
      return {...state};
    }
    default: // Do nothing
  }

  switch (action.type) {
    case RunsInboxModalActionType.CHANGE_FILTERS: {
      const {filters} = action;
      return {...state, filters};
    }
    case RunsInboxModalActionType.RESET_STATE: {
      return RUNS_INBOX_MODAL_INITIAL_STATE;
    }
    case RunsInboxModalActionType.SELECT_RUN: {
      const {runId} = action;
      return {...state, selectedRunId: runId};
    }
    default:
      return state;
  }
}

export default reduceRunsInboxModal;
